/**
 * Inline "not available for your role" state.
 *
 * The sidebar dims destinations a role cannot open, but a URL can still be
 * typed, bookmarked or shared. Rendering this instead of the page keeps the
 * refusal in one calm place and — importantly — fires no requests, so the user
 * never sees a permission toast for something they did not do.
 *
 * Deliberately not a redirect: silently bouncing someone to the dashboard reads
 * as the app losing their click.
 */

import { Lock } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/PageHeader";

export function RoleGate({
  title,
  requirement,
  inSection = false,
}: {
  /** The destination they tried to open, e.g. "Users". */
  title: string;
  /** Who may open it, e.g. "MSP admins and above". */
  requirement: string;
  /**
   * Shown inside a page that already has its own header, such as a section of
   * Settings, so it draws no second page title.
   */
  inSection?: boolean;
}) {
  return (
    <div className="space-y-6">
      {!inSection && <PageHeader title={title} />}
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-border bg-card px-6 py-16 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
          <Lock className="h-5 w-5 text-muted-foreground" />
        </div>
        {/* Announced as it appears, so a keyboard or screen-reader user who
            followed a link learns at once that it was refused. */}
        <div role="alert" className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            {title} is available to {requirement}.
          </p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Your account does not have access. An administrator can change your role or add
            you to a group that grants it.
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="mt-1">
          <Link to="/">Back to dashboard</Link>
        </Button>
      </div>
    </div>
  );
}
